import { clsx, type ClassValue } from "clsx";
import { format } from "date-fns";
import { get } from "lodash";
import { Archive, CheckCircle, ClipboardList, Clock, TrendingUp, Users } from "lucide-react";
import { twMerge } from "tailwind-merge";

import {
  OrderPageResponseDTOStatus,
  OrderPageAllTradeCargoResponseDTOStatus,
  AggregateResponseDTO,
  AuthUserDTORole,
} from "@/common/api";
import { DATETIME_FORMAT, ROLE_LABELS, TARIFF_LABELS } from "@/common/model";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatDatetime = (date: Date | string | null | undefined) =>
  date ? format(date, "dd.MM.yyyy HH:mm") : null;

export const formatDatetimeLocal = (date: Date | string | null | undefined) =>
  date ? format(date, DATETIME_FORMAT) : null;

export const formatDate = (date: Date | string | null | undefined) =>
  date ? format(date, "dd.MM.yyyy") : null;

export const pluralize = (n: number | null | undefined, one: string, few: string, many: string) => {
  if (n === null || n === undefined) return null;

  const abs = Math.abs(n) % 100;
  const last = abs % 10;

  if (abs > 10 && abs < 20) return `${n} ${many}`; // 11–19
  if (last === 1) return `${n} ${one}`; // 1, 21, 31...
  if (last >= 2 && last <= 4) return `${n} ${few}`; // 2–4, 22–24...

  return `${n} ${many}`; // 0, 5–9, 10, 11–19, 25–30...
};

export const getTariffLabel = (tariff?: string): string => {
  if (!tariff) return "-";
  return get(TARIFF_LABELS, tariff, tariff);
};

export const getRoleLabel = (role: string): string => {
  const normalizedRole = role.toLowerCase();
  const roleKey = normalizedRole as keyof typeof ROLE_LABELS;

  return ROLE_LABELS[roleKey] || role;
};

export const getStatusData = (
  status?: OrderPageResponseDTOStatus | OrderPageAllTradeCargoResponseDTOStatus | string,
) => {
  switch (status) {
    case OrderPageResponseDTOStatus.CANCELLED:
    case OrderPageAllTradeCargoResponseDTOStatus.CANCELLED:
    case "CANCELLED":
      return { color: "text-Text-main bg-Status-light_gray", label: "Отменено" };
    case OrderPageResponseDTOStatus.COLLECT_PROPOSAL:
    case OrderPageAllTradeCargoResponseDTOStatus.COLLECT_PROPOSAL:
    case "COLLECT_PROPOSAL":
      return { color: "bg-green-100 text-green-800", label: "Сбор предложений" };
    case OrderPageResponseDTOStatus.COMPLETED:
    case OrderPageAllTradeCargoResponseDTOStatus.COMPLETED:
    case "COMPLETED":
      return { color: "bg-Secondary-light-blue text-Secondary-deep-blue", label: "Завершено" };
    case OrderPageResponseDTOStatus.DRAFT:
    case OrderPageAllTradeCargoResponseDTOStatus.DRAFT:
    case "DRAFT":
      return {
        color: "text-Status-light_gray bg-Buttons-btn-icon-btn_icon_disabled",
        label: "Черновик",
      };
    case OrderPageResponseDTOStatus.ON_MODERATION:
    case OrderPageAllTradeCargoResponseDTOStatus.ON_MODERATION:
    case "ON_MODERATION":
      return { color: "bg-Secondary-light-amber text-Secondary-deep-amber", label: "На модерации" };
    case OrderPageResponseDTOStatus.ON_REVISION:
    case OrderPageAllTradeCargoResponseDTOStatus.ON_REVISION:
    case "ON_REVISION":
      return {
        color: "text-Secondary-deep-violet bg-Secondary-light-violet",
        label: "На доработке",
      };
    case OrderPageResponseDTOStatus.REBIDDING:
    case OrderPageAllTradeCargoResponseDTOStatus.REBIDDING:
    case "REBIDDING":
      return { color: "", label: "Переторжка" };
    case OrderPageResponseDTOStatus.SUMMING_UP:
    case OrderPageAllTradeCargoResponseDTOStatus.SUMMING_UP:
    case "SUMMING_UP":
      return {
        color: "text-Secondary-deep-red bg-Secondary-light-red",
        label: "Подведение итогов",
      };
    case OrderPageResponseDTOStatus.WAITING_TRADE:
    case OrderPageAllTradeCargoResponseDTOStatus.WAITING_TRADE:
    case "WAITING_TRADE":
      return {
        color:
          "text-Text-main bg-Background-white rounded-full outline outline-1 outline-offset-[-1px] outline-Border-gray",
        label: "В ожидании",
      };
    default:
      return { color: "", label: "" };
  }
};

export const getFilterOnStatus = (status: AggregateResponseDTO) => {
  switch (status.code) {
    case "ALL":
      return {
        title: "Всего: заявки",
        value: status.code,
        count: status.count,
        id: "",
        icon: ClipboardList,
        className: "bg-blue-500 text-white",
      };
    case "ON_MODERATION":
      return {
        title: "На модерации",
        value: status.code,
        count: status.count,
        id: status.id,
        icon: Clock,
        className: "bg-amber-500 text-white",
      };
    case "COLLECT_PROPOSAL":
      return {
        title: "Опубликовано: этап Сбор предложений",
        value: status.code,
        count: status.count,
        id: status.id,
        icon: TrendingUp,
        className: "bg-green-500 text-white",
      };
    case "REBIDDING":
      return {
        title: "Опубликовано: этап Переторжка",
        value: status.code,
        count: status.count,
        id: status.id,
        icon: Users,
        className: "bg-orange-500 text-white",
      };
    case "SUMMING_UP":
      return {
        title: "Опубликовано: этап Подведение итогов",
        value: status.code,
        count: status.count,
        id: status.id,
        icon: CheckCircle,
        className: "bg-purple-500 text-white",
      };
    case "COMPLETED":
      return {
        title: "Завершено",
        value: status.code,
        count: status.count,
        id: status.id,
        icon: Archive,
        className: "bg-gray-500 text-white",
      };
    default:
      return { title: "", value: "", count: "-", id: "", icon: ClipboardList, className: "" };
  }
};

export interface GetAppTextReturn {
  title: string;
  subtitle: string;
}

export const getAppText = (role: string | null): GetAppTextReturn => {
  switch (role) {
    case AuthUserDTORole.CARGO_OWNER:
      return {
        title: "Личный кабинет грузовладельца",
        subtitle: "Мои заявки на перевозки груза",
      };

    case AuthUserDTORole.WAGON_OWNER:
      return {
        title: "Заявки для участия",
        subtitle: "",
      };

    case AuthUserDTORole.ADMIN:
      return {
        title: "Панель администратора",
        subtitle: "Заявки в системе",
      };

    default:
      return { title: "", subtitle: "" };
  }
};
